import classNames from "classnames";
import { useMemo } from "react";
import AscButton from "../AscButton/AscButton";
import {
  CalendarFilledIcon,
  CompetitionIcon,
  StarIcon,
  TestIcon,
} from "../AscIcons";
import { QuestionMarkIllustration } from "../AscIllustrations";
import AscLink from "../AscLink/AscLink";
import styles from "./AscAnalysisWeeklyPoints.module.scss";
import { roundAndDropTrailingZeros } from "@ascendnow/shared";

const EMPTY_RECORD = { points: 0, increment: 0 };

export type PointType = "challenge" | "event" | "competition" | "module";
export interface AscAnalysisWeeklyPointsProps {
  className?: string;
  challenge?: Partial<Record<"points" | "increment", number>>;
  event?: Partial<Record<"points" | "increment", number>>;
  competition?: Partial<Record<"points" | "increment", number>>;
  module?: Partial<Record<"points" | "increment", number>>;
  noStatsTitle?: string;
  noStatsDescription?: string;
  noStatsActionButtonText?: string;
  noStatsActionLink?: string;
  isEmpty?: boolean;
  loading?: boolean;
}

const convertIfPositive = (value: number) => (value >= 0 ? "+" + value : value);

const getPointIcon = (type: PointType, className?: string) => {
  switch (type) {
    case "challenge":
      return <StarIcon className={className} />;
    case "event":
      return <CalendarFilledIcon className={className} />;
    case "competition":
      return <CompetitionIcon className={className} />;
    case "module":
    default:
      return <TestIcon className={className} />;
  }
};

const AscAnalysisWeeklyPoint = ({
  type,
  points,
  pointsChange,
  title,
}: {
  type: PointType;
  points: number;
  pointsChange: number;
  title: string;
}) => {
  return (
    <div className={classNames(styles.pointsContainer, styles[type + "s"])}>
      <div className={styles.pointsIconWrapper}>
        {getPointIcon(type, styles.pointsIcon)}
      </div>
      <div className={styles.pointsValueContainer}>
        <span className={styles.pointsValue}>
          {roundAndDropTrailingZeros(points)}
        </span>
        {pointsChange !== null && pointsChange !== undefined && (
          <span
            className={classNames(
              styles.point,
              pointsChange === 0 && styles["point--zero"],
              pointsChange < 0 && styles["point--increment"],
              pointsChange < 0 && styles["point--decrement"]
            )}
          >
            ({convertIfPositive(roundAndDropTrailingZeros(pointsChange))})
          </span>
        )}
      </div>
      <div className={styles.pointsLabel}>{title}</div>
    </div>
  );
};

export default function AscAnalysisWeeklyPoints({
  loading = false,
  className,
  challenge = EMPTY_RECORD,
  event = EMPTY_RECORD,
  competition = EMPTY_RECORD,
  module = EMPTY_RECORD,
  noStatsTitle = "No stats",
  noStatsDescription = "You don't have any statistics at the moment. Start your first lesson on the platform!",
  noStatsActionButtonText = "Let's start!",
  noStatsActionLink,
  isEmpty,
}: AscAnalysisWeeklyPointsProps) {
  return (
    <div
      data-testid="asc-analysis-weekly-points"
      className={classNames(
        styles.ground,
        {
          [styles.empty]: isEmpty,
          [styles.loading]: loading,
        },
        className
      )}
    >
      <div className={styles.emptyContainer}>
        <QuestionMarkIllustration className={styles.emptyIcon} />
        <div className={styles.emptyTitle}>{noStatsTitle}</div>
        <div className={styles.emptyDescription}>{noStatsDescription}</div>
        <AscLink href={noStatsActionLink} disabled={!noStatsActionLink}>
          <AscButton>{noStatsActionButtonText}</AscButton>
        </AscLink>
      </div>
      <AscAnalysisWeeklyPoint
        {...{
          type: "challenge",
          title: "for challenges",
          points: challenge.points || 0,
          pointsChange: challenge.increment || 0,
        }}
      />
      <AscAnalysisWeeklyPoint
        {...{
          type: "event",
          title: "for events",
          points: event.points || 0,
          pointsChange: event.increment || 0,
        }}
      />
      <AscAnalysisWeeklyPoint
        {...{
          type: "competition",
          title: "for competitions",
          points: competition.points || 0,
          pointsChange: competition.increment || 0,
        }}
      />
      <AscAnalysisWeeklyPoint
        {...{
          type: "module",
          title: "for modules",
          points: module.points || 0,
          pointsChange: module.increment || 0,
        }}
      />
    </div>
  );
}
